/**
 * Interface Admin - Analytics & Reporting Avancés
 * Business Intelligence et analytics complets pour l'administration
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Tab,
  Tabs,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  LinearProgress,
  CircularProgress,
  List,
  ListItem,
  ListItemText as MuiListItemText,
  Switch,
  FormControlLabel,
  Slider,
  AccordionDetails,
  AccordionSummary,
  Accordion,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as ReportIcon,
  Timeline as TimelineIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  ShowChart as LineChartIcon,
  GetApp as ExportIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Visibility as ViewIcon,
  DateRange as DateRangeIcon,
  Group as GroupIcon,
  MonetizationOn as MoneyIcon,
  Business as BusinessIcon,
  Speed as SpeedIcon,
  Insights as InsightsIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
  Schedule as ScheduleIcon,
  Compare as CompareIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  Dashboard as DashboardIcon,
  Email as EmailIcon,
  Print as PrintIcon,
  Share as ShareIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import dayjs from 'dayjs';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const AdminAnalytics = () => {
  const { user } = useAuth();
  
  // Charger l'onglet mémorisé depuis localStorage ou utiliser 0 par défaut
  const [tabValue, setTabValue] = useState(() => {
    const savedTab = localStorage.getItem('adminAnalyticsTab');
    return savedTab ? parseInt(savedTab, 10) : 0;
  });

  // Mémoriser l'onglet actif dans localStorage quand il change
  useEffect(() => {
    localStorage.setItem('adminAnalyticsTab', tabValue.toString());
  }, [tabValue]);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [userAnalytics, setUserAnalytics] = useState(null);
  const [revenueAnalytics, setRevenueAnalytics] = useState(null);
  const [businessAlerts, setBusinessAlerts] = useState([]);
  const [cohortData, setCohortData] = useState(null);
  const [forecastingData, setForecastingData] = useState(null);
  const [benchmarkData, setBenchmarkData] = useState(null);
  
  const [dateRange, setDateRange] = useState({
    from: dayjs().subtract(30, 'day'), // 30 jours
    to: dayjs()
  });
  
  const [filters, setFilters] = useState({
    period: '30d',
    segment: 'all',
    metrics: 'all',
    city: '',
    category: ''
  });

  const [customReportDialog, setCustomReportDialog] = useState(false);
  const [customReportConfig, setCustomReportConfig] = useState({
    title: '',
    metrics: [],
    filters: {},
    format: 'pdf',
    schedule: false
  });

  useEffect(() => {
    loadAnalyticsDashboard();
    loadUserAnalytics();
    loadRevenueAnalytics();
    loadBusinessAlerts();
    loadCohortAnalysis();
    loadBenchmarkData();
  }, [dateRange, filters]);

  const loadAnalyticsDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/analytics/dashboard', {
        params: { ...filters, dateFrom: dateRange.from, dateTo: dateRange.to }
      });
      setDashboardData(response.data.data);
    } catch (error) {
      console.error('Erreur chargement dashboard analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserAnalytics = async () => {
    try {
      const response = await api.get('/admin/analytics/users', {
        params: { ...filters, dateFrom: dateRange.from, dateTo: dateRange.to }
      });
      setUserAnalytics(response.data.data);
    } catch (error) {
      console.error('Erreur chargement analytics utilisateurs:', error);
    }
  };

  const loadRevenueAnalytics = async () => {
    try {
      const response = await api.get('/admin/analytics/revenue', {
        params: { ...filters, dateFrom: dateRange.from, dateTo: dateRange.to }
      });
      setRevenueAnalytics(response.data.data);
    } catch (error) {
      console.error('Erreur chargement analytics revenus:', error);
    }
  };

  const loadBusinessAlerts = async () => {
    try {
      const response = await api.get('/admin/analytics/alerts');
      setBusinessAlerts(response.data.data);
    } catch (error) {
      console.error('Erreur chargement alertes business:', error);
    }
  };

  const loadCohortAnalysis = async () => {
    try {
      const response = await api.get('/admin/analytics/users/cohorts');
      setCohortData(response.data.data);
    } catch (error) {
      console.error('Erreur chargement analyse cohortes:', error);
    }
  };

  const loadBenchmarkData = async () => {
    try {
      const response = await api.get('/admin/analytics/benchmark');
      setBenchmarkData(response.data.data);
    } catch (error) {
      console.error('Erreur chargement benchmark:', error);
    }
  };

  const loadForecasting = async () => {
    try {
      const response = await api.get('/admin/analytics/revenue/forecasting');
      setForecastingData(response.data.data);
    } catch (error) {
      console.error('Erreur chargement prévisions:', error);
    }
  };

  const generateCustomReport = async () => {
    try {
      const response = await api.post('/admin/analytics/reports/custom', customReportConfig);
      // Télécharger le rapport généré
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `custom_report.${customReportConfig.format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setCustomReportDialog(false);
    } catch (error) {
      console.error('Erreur génération rapport:', error);
    }
  };

  const exportAnalyticsData = async (type, format = 'excel') => {
    try {
      const response = await api.get('/admin/analytics/export', {
        params: { type, format, ...filters, dateFrom: dateRange.from, dateTo: dateRange.to },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics_${type}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Erreur export analytics:', error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const getMetricColor = (value, benchmark) => {
    if (value > benchmark * 1.1) return 'success.main';
    if (value < benchmark * 0.9) return 'error.main';
    return 'warning.main';
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        {/* En-tête */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" gutterBottom>
            📊 Analytics & Reporting Avancés
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                loadAnalyticsDashboard();
                loadUserAnalytics();
                loadRevenueAnalytics();
              }}
            >
              Actualiser
            </Button>
            <Button
              variant="outlined"
              startIcon={<ReportIcon />}
              onClick={() => setCustomReportDialog(true)}
            >
              Rapport Personnalisé
            </Button>
            <Button
              variant="contained"
              startIcon={<ExportIcon />}
              onClick={() => exportAnalyticsData('comprehensive')}
            >
              Export Complet
            </Button>
          </Box>
        </Box>

        {/* Filtres globaux */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={2}>
                <DatePicker
                  label="Date début"
                  value={dateRange.from}
                  onChange={(date) => setDateRange({...dateRange, from: date})}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <DatePicker
                  label="Date fin"
                  value={dateRange.to}
                  onChange={(date) => setDateRange({...dateRange, to: date})}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Période</InputLabel>
                  <Select
                    value={filters.period}
                    onChange={(e) => setFilters({...filters, period: e.target.value})}
                  >
                    <MenuItem value="7d">7 jours</MenuItem>
                    <MenuItem value="30d">30 jours</MenuItem>
                    <MenuItem value="90d">3 mois</MenuItem>
                    <MenuItem value="1y">1 an</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Segment</InputLabel>
                  <Select
                    value={filters.segment}
                    onChange={(e) => setFilters({...filters, segment: e.target.value})}
                  >
                    <MenuItem value="all">Tous</MenuItem>
                    <MenuItem value="new_users">Nouveaux</MenuItem>
                    <MenuItem value="returning_users">Récurrents</MenuItem>
                    <MenuItem value="premium_users">Premium</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    loadAnalyticsDashboard();
                    loadUserAnalytics();
                    loadRevenueAnalytics();
                  }}
                  startIcon={<FilterIcon />}
                >
                  Appliquer
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Alertes business */}
        {businessAlerts.length > 0 && (
          <Box sx={{ mb: 3 }}>
            {businessAlerts.slice(0, 3).map((alert, index) => (
              <Alert key={index} severity={alert.severity} sx={{ mb: 1 }}>
                <Typography variant="body2">
                  <strong>{alert.title}:</strong> {alert.message}
                </Typography>
              </Alert>
            ))}
          </Box>
        )}

        {/* Onglets */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
            <Tab label="Vue d'Ensemble" icon={<DashboardIcon />} />
            <Tab label="Utilisateurs" icon={<GroupIcon />} />
            <Tab label="Revenus" icon={<MoneyIcon />} />
            <Tab label="Marketing" icon={<BusinessIcon />} />
            <Tab label="Prévisions" icon={<TimelineIcon />} />
            <Tab label="Benchmark" icon={<CompareIcon />} />
          </Tabs>
        </Paper>

        {/* Vue d'Ensemble */}
        <TabPanel value={tabValue} index={0}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : dashboardData && (
            <Grid container spacing={3}>
              {/* KPIs principaux */}
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <GroupIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6">Utilisateurs Actifs</Typography>
                    </Box>
                    <Typography variant="h4">
                      {dashboardData.activeUsers?.toLocaleString() || 0}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      {dashboardData.userGrowth > 0 ? (
                        <TrendingUpIcon color="success" />
                      ) : (
                        <TrendingDownIcon color="error" />
                      )}
                      <Typography 
                        variant="body2" 
                        color={dashboardData.userGrowth > 0 ? 'success.main' : 'error.main'}
                      >
                        {dashboardData.userGrowth?.toFixed(1) || 0}%
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <MoneyIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6">Revenus</Typography>
                    </Box>
                    <Typography variant="h4">
                      {formatCurrency(dashboardData.totalRevenue || 0)}
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={dashboardData.revenueProgress || 0}
                      sx={{ mt: 1 }}
                    />
                    <Typography variant="caption" color="textSecondary">
                      Objectif mensuel: {formatCurrency(dashboardData.monthlyTarget || 0)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <SpeedIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6">Conversion</Typography>
                    </Box>
                    <Typography variant="h4">
                      {formatPercentage(dashboardData.conversionRate || 0)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Benchmark: {formatPercentage(0.15)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <InsightsIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6">Score Santé</Typography>
                    </Box>
                    <Typography variant="h4" color={getMetricColor(dashboardData.healthScore, 80)}>
                      {dashboardData.healthScore || 0}/100
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={dashboardData.healthScore || 0}
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Graphiques principaux */}
              <Grid item xs={12} md={8}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Évolution des Métriques Clés
                    </Typography>
                    <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography color="textSecondary">
                        Graphique des tendances principales
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Top Métriques
                    </Typography>
                    <List dense>
                      {dashboardData.topMetrics?.map((metric, index) => (
                        <ListItem key={index}>
                          <Box sx={{ width: '100%' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">
                                {metric.name}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {metric.value}
                              </Typography>
                            </Box>
                            <LinearProgress 
                              variant="determinate" 
                              value={metric.progress || 0}
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        </ListItem>
                      )) || (
                        <Typography color="textSecondary">
                          Aucune donnée disponible
                        </Typography>
                      )}
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Insights & Recommandations */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Insights & Recommandations IA
                    </Typography>
                    <Grid container spacing={2}>
                      {dashboardData.insights?.map((insight, index) => (
                        <Grid item xs={12} md={4} key={index}>
                          <Alert severity={insight.type} sx={{ height: '100%' }}>
                            <Typography variant="body2">
                              <strong>{insight.title}</strong>
                            </Typography>
                            <Typography variant="body2">
                              {insight.description}
                            </Typography>
                            {insight.action && (
                              <Button size="small" sx={{ mt: 1 }}>
                                {insight.action}
                              </Button>
                            )}
                          </Alert>
                        </Grid>
                      )) || (
                        <Grid item xs={12}>
                          <Typography color="textSecondary">
                            Aucun insight disponible pour la période sélectionnée
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </TabPanel>

        {/* Analytics Utilisateurs */}
        <TabPanel value={tabValue} index={1}>
          {userAnalytics && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Acquisition Utilisateurs
                    </Typography>
                    <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography color="textSecondary">
                        Graphique d'acquisition
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Rétention par Cohorte
                    </Typography>
                    <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography color="textSecondary">
                        Heatmap de rétention
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Segmentation Utilisateurs
                    </Typography>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Segment</TableCell>
                            <TableCell>Utilisateurs</TableCell>
                            <TableCell>Revenus</TableCell>
                            <TableCell>LTV</TableCell>
                            <TableCell>Rétention</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {userAnalytics.segments?.map((segment, index) => (
                            <TableRow key={index}>
                              <TableCell>{segment.name}</TableCell>
                              <TableCell>{segment.users?.toLocaleString()}</TableCell>
                              <TableCell>{formatCurrency(segment.revenue || 0)}</TableCell>
                              <TableCell>{formatCurrency(segment.ltv || 0)}</TableCell>
                              <TableCell>{formatPercentage(segment.retention || 0)}</TableCell>
                            </TableRow>
                          )) || (
                            <TableRow>
                              <TableCell colSpan={5} align="center">
                                <Typography color="textSecondary">
                                  Aucune donnée de segmentation disponible
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </TabPanel>

        {/* Analytics Revenus */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Analytics Revenus & Performance Financière
          </Typography>
          <Typography color="textSecondary">
            Interface d'analytics revenus en développement...
          </Typography>
        </TabPanel>

        {/* Analytics Marketing */}
        <TabPanel value={tabValue} index={3}>
          <Typography variant="h6" gutterBottom>
            Analytics Marketing & Attribution
          </Typography>
          <Typography color="textSecondary">
            Interface d'analytics marketing en développement...
          </Typography>
        </TabPanel>

        {/* Prévisions */}
        <TabPanel value={tabValue} index={4}>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              onClick={loadForecasting}
              startIcon={<TimelineIcon />}
            >
              Générer Prévisions
            </Button>
          </Box>
          {forecastingData && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Prévisions de Revenus (3 mois)
                    </Typography>
                    <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography color="textSecondary">
                        Graphique de prévisions
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </TabPanel>

        {/* Benchmark */}
        <TabPanel value={tabValue} index={5}>
          {benchmarkData && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Comparaison Sectorielle
                    </Typography>
                    <Typography color="textSecondary">
                      Interface de benchmark en développement...
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </TabPanel>

        {/* Dialog de rapport personnalisé */}
        <Dialog 
          open={customReportDialog} 
          onClose={() => setCustomReportDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Créer un Rapport Personnalisé</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Titre du rapport"
                  value={customReportConfig.title}
                  onChange={(e) => setCustomReportConfig({
                    ...customReportConfig,
                    title: e.target.value
                  })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Format</InputLabel>
                  <Select
                    value={customReportConfig.format}
                    onChange={(e) => setCustomReportConfig({
                      ...customReportConfig,
                      format: e.target.value
                    })}
                  >
                    <MenuItem value="pdf">PDF</MenuItem>
                    <MenuItem value="excel">Excel</MenuItem>
                    <MenuItem value="ppt">PowerPoint</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={customReportConfig.schedule}
                      onChange={(e) => setCustomReportConfig({
                        ...customReportConfig,
                        schedule: e.target.checked
                      })}
                    />
                  }
                  label="Rapport programmé"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCustomReportDialog(false)}>
              Annuler
            </Button>
            <Button 
              variant="contained" 
              onClick={generateCustomReport}
              disabled={!customReportConfig.title}
            >
              Générer Rapport
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default AdminAnalytics;
